import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { toElasticQuery } from '@alliance-disposal/wql';
import axios from 'axios';
import { parseAsInteger, parseAsJson, useQueryStates } from 'nuqs';
import { FormProvider, useForm } from 'react-hook-form';
import { countFilterFields, filterUnusedBooleanFields, findObjectsWithField, handleCheckIfEqualToDefault, handlePrepareQuery, isNumberStrict, } from '../helpers/functions';
import { SourQuerySchema } from '../helpers/schema';
import { SourSearchAppContext } from './app-context';
// Define the initial state of the context
const initialValue = {
    environmentOptions: {
        apiKey: '',
        application: 'aap',
        environment: 'development',
    },
    results: undefined,
    searchActive: false,
    filterActive: false,
    searchOrFilterActive: false,
    formInitialized: false,
    setSearchActive: () => { },
    setFilterActive: () => { },
    defaultFilters: undefined,
    setDefaultFilters: () => { },
    isLoading: false,
    filtersOpen: false,
    highlightActive: false,
    handleResetDefaultFilters: () => { },
    onResetValues: () => { },
    resetValues: null,
    setFiltersOpen: () => { },
    onSubmit: () => { },
    onNavigate: () => { },
    onSeeAllClick: () => { },
    page: 0,
    size: 50,
    setPage: () => { },
    setSize: () => { },
    searchResponse: undefined,
    showEasyFilter: false,
    searchTerm: '',
    setSearchTerm: () => { },
    onGlobalOnSeeAllClick: () => { },
    lockFilterType: false,
};
// Create the context
export const SourSearchContext = createContext(initialValue);
// Create a provider component
export const SourSearchProvider = ({ children, onNavigate: externalNavigate, restrictTo, highlightActive, createQueryParams, onSeeAllClick, initialValues, showEasyFilter = false, lockFilterType = false, }) => {
    const { environmentOptions, globalRestrictTo, onGlobalOnSeeAllClick } = useContext(SourSearchAppContext);
    const restriction = restrictTo || globalRestrictTo || undefined;
    const [results, setResults] = useState();
    const [isLoading, setIsLoading] = useState(false);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [searchActive, setSearchActive] = useState(false);
    const [filterActive, setFilterActive] = useState(false);
    const [searchOrFilterActive, setSearchOrFilterActive] = useState(false);
    const [formInitialized, setFormInitialized] = useState(false);
    const [resetValues, setResetValues] = useState(null);
    // lastPage is used to stop duplicate fetches if page is set from SourSearch rather than the parent
    const [lastPage, setLastPage] = useState(undefined);
    const [lastSize, setLastSize] = useState(undefined);
    // New
    const [page, setPage] = useState(initialValues?.page || 0);
    const [size, setSize] = useState(initialValues?.size || 50);
    const [searchResponse, setSearchResponse] = useState(undefined);
    const [defaultFilters, setDefaultFilters] = useState(initialValues?.defaultFilters);
    const [searchTerm, setSearchTerm] = useState('');
    // State for handlers
    const [onResultsHandler, setOnResultsHandler] = useState();
    const registerOnResultsHandler = useCallback((handler) => {
        setOnResultsHandler(() => handler);
    }, []);
    //FORM CONTEXT
    const methods = useForm({
        shouldUnregister: true,
        defaultValues: {
            type: initialValues?.defaultFilters?.query?.type || undefined,
        },
    });
    const { setValue, getValues } = methods;
    // nuqs query state for SourQuery, page, and size
    const [queryParams, setQueryParams] = useQueryStates({
        sourQuery: parseAsJson(SourQuerySchema.parse),
        searchPage: parseAsInteger.withDefault(initialValues?.page ?? 0),
        searchSize: parseAsInteger.withDefault(initialValues?.size ?? 50),
    });
    // Destructure for convenience
    const { sourQuery, searchPage: nuqsPage } = queryParams;
    // --- handleCreateQueryParams logic extracted from handleSubmit ---
    const handleCreateQueryParams = useCallback((queryData, searchPage, searchSize) => {
        if (!createQueryParams)
            return;
        const filterCount = countFilterFields(queryData);
        if ((filterCount.totalCount === 0 && !queryData.query?.query) ||
            (createQueryParams.removeOn === 'empty_string_inactive_filter' &&
                !queryData?.query?.query &&
                handleCheckIfEqualToDefault(queryData, defaultFilters))) {
            setQueryParams({ sourQuery: null, searchPage: 0, searchSize: searchSize });
        }
        else {
            setQueryParams({
                sourQuery: createQueryParams.method === 'with_filter' ? (filterCount.totalCount > 0 ? queryData : null) : queryData,
                ...(createQueryParams.stopPageAdd ? {} : { searchPage: searchPage }),
                searchSize: searchSize,
            });
        }
    }, [createQueryParams, setQueryParams, defaultFilters]);
    // --- end handleCreateQueryParams logic ---
    const handleSubmit = async (data, passedPage) => {
        const queryData = { ...data };
        if (environmentOptions.application === undefined || environmentOptions.environment === undefined) {
            return;
        }
        setIsLoading(true);
        if (filtersOpen) {
            setFiltersOpen(false);
        }
        const searchSize = size || 50;
        const searchPage = passedPage ?? page ?? 0;
        // --- createQueryParams logic ---
        handleCreateQueryParams(queryData, searchPage, searchSize);
        // --- end createQueryParams logic ---
        // CHECK TO SEE IF ANY FILTERS OR SEARCH SHOULD BE ACTIVE - TODO can this eliminate any other functions here?
        if (handleCheckIfEqualToDefault(queryData, defaultFilters) && !defaultFilters?.activateOnInitial) {
            setFilterActive(false);
            if (!queryData.query?.query)
                setSearchActive(false);
        }
        const parsedQuery = handlePrepareQuery(queryData.query, environmentOptions.application);
        // PREPARE QUERY FOR ELASTIC
        const query = {
            ...queryData,
            filters: filterUnusedBooleanFields(findObjectsWithField(queryData.filters)),
            sort: queryData.sort?.field === undefined ? undefined : queryData.sort,
            query: parsedQuery.query,
        };
        const elasticQuery = toElasticQuery(query, {
            application: environmentOptions.application,
            environment: environmentOptions.environment,
            haulerID: environmentOptions.application === 'wayste' ? restriction : undefined,
            customerID: environmentOptions.application === 'customer-portal' ? restriction : undefined,
            multiMatchType: parsedQuery.multiMatchType,
        });
        const requestPath = Array.isArray(elasticQuery.index) ? elasticQuery.index.join(',') : elasticQuery.index;
        try {
            const esUrl = environmentOptions.environment === 'development'
                ? `http://localhost:9200/${requestPath}/_search`
                : `https://sourgum-search.es.us-east-1.aws.found.io/${requestPath}/_search`;
            const searchResults = await axios.post(esUrl, {
                query: elasticQuery.query,
                aggs: elasticQuery.aggs,
                sort: elasticQuery.sort,
            }, {
                headers: {
                    Authorization: `ApiKey ${environmentOptions.apiKey}`,
                },
                params: {
                    size: searchSize,
                    from: searchPage ? searchSize * searchPage || 0 : 0,
                },
            });
            const totalHits = isNumberStrict(searchResults.data.hits.total)
                ? searchResults.data.hits.total
                : searchResults.data?.hits?.total?.value || 0;
            const totalPages = Math.ceil(totalHits / searchSize);
            setIsLoading(false);
            return {
                results: searchResults.data,
                page: searchPage,
                size: searchSize,
                totalPages,
                totalHits,
                type: queryData.type,
            };
        }
        catch (error) {
            console.warn('onSubmit ERROR', error);
            setIsLoading(false);
        }
    };
    // PREPARE QUERY FOR ELASTIC && EXPOSES INITIAL RESULTS TO EXTERNAL COMPONENTS
    const onSubmit = async (data, passedPage) => {
        setLastPage(passedPage || 0);
        setLastSize(size || 50);
        const response = await handleSubmit(data, passedPage || 0);
        setResults(response?.results);
        setSearchResponse(response);
        if (response && onResultsHandler)
            onResultsHandler({ ...response });
    };
    // FETCHES MORE RESULTS BASED ON CURRENT QUERY && EXPOSES PAGINATED > 0 RESULTS TO EXTERNAL COMPONENTS
    const handleGetMoreResults = async () => {
        if (page === undefined || size === undefined || (page === lastPage && size === lastSize)) {
            return;
        }
        setLastPage(page);
        setLastSize(size || 50);
        const data = getValues();
        const response = await handleSubmit(data);
        setSearchResponse(response);
        if (response && onResultsHandler) {
            onResultsHandler({ ...response });
        }
    };
    // EXPOSE MORE RESULTS ON PAGE OR SIZE CHANGE
    useEffect(() => {
        if (formInitialized)
            handleGetMoreResults();
    }, [page, size]);
    // HANDLES RESETTING FILTER COMPONENTS ON CLICK AWAY
    const onResetValues = (fields) => {
        setResetValues(fields);
        if (fields === 'all' || fields === 'search')
            setSearchTerm('');
        setTimeout(() => {
            setResetValues(null);
        }, 50);
    };
    // HANDLE RESET DEFAULTS CLICK IF THERE ARE DEFAULTS
    const handleResetDefaultFilters = () => {
        if (defaultFilters?.activateOnInitial && defaultFilters?.query) {
            onSubmit(defaultFilters.query);
            setFilterActive(true);
            onResetValues('all');
            if (createQueryParams) {
                handleCreateQueryParams(defaultFilters.query, 0, size);
            }
        }
        else {
            setFilterActive(false);
            setSearchActive(false);
            onResetValues('all');
            if (createQueryParams) {
                setQueryParams({
                    sourQuery: null,
                    searchPage: 0,
                    searchSize: size,
                });
            }
        }
    };
    useEffect(() => {
        setValue('type', defaultFilters?.query?.type || undefined);
        onResetValues('filters');
        if (defaultFilters?.activateOnInitial) {
            const data = getValues();
            onSubmit({
                ...defaultFilters.query,
                query: data.query,
            });
        }
    }, [defaultFilters]);
    // SETS STARTING VALUES BASED ON QUERY STRING OR DEFAULT FILTERS
    useEffect(() => {
        if (createQueryParams && sourQuery) {
            if (showEasyFilter &&
                sourQuery.query?.query &&
                typeof sourQuery.query.query === 'string' &&
                sourQuery.query.query.includes(':')) {
                const inputArray = [
                    sourQuery.query.query.slice(0, sourQuery.query.query.indexOf(':')),
                    sourQuery.query.query.slice(sourQuery.query.query.indexOf(':') + 1),
                ];
                const searchValue = inputArray[1];
                setValue('query', {
                    query: searchValue,
                    restrictToFields: sourQuery.query.restrictToFields,
                });
            }
            else {
                setValue('query', sourQuery.query);
            }
            setValue('filters', sourQuery.filters);
            setValue('sort', sourQuery.sort);
            setValue('type', sourQuery.type);
            onSubmit(sourQuery, nuqsPage);
            if (!handleCheckIfEqualToDefault(sourQuery, defaultFilters) || defaultFilters?.activateOnInitial) {
                setFilterActive(true);
                if (sourQuery.query?.query)
                    setSearchActive(true);
            }
        }
        else if (defaultFilters) {
            setValue('type', defaultFilters?.query?.type || undefined);
            if (defaultFilters?.query?.filters)
                setValue('filters', defaultFilters.query.filters);
            if (defaultFilters?.query?.sort)
                setValue('sort', defaultFilters.query.sort);
            if (defaultFilters.activateOnInitial && defaultFilters.query) {
                onSubmit(defaultFilters.query);
                setFilterActive(true);
                if (defaultFilters.query?.query)
                    setSearchActive(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    // NEEDED TO BE ABLE TO SET STARTING AND DEFAULT FILTER VALUES
    useEffect(() => {
        setTimeout(() => {
            setFormInitialized(true);
        }, 500);
    }, []);
    //EXPOSES SEARCH ACTIVE TO EXTERNAL COMPONENTS
    useEffect(() => {
        setSearchOrFilterActive(searchActive || filterActive);
        if (formInitialized && !searchActive && !filterActive) {
            setQueryParams({
                sourQuery: null,
                searchPage: 0,
                searchSize: initialValues?.size || 50,
            });
        }
    }, [searchActive, filterActive]);
    // when default values are passed in need to set it to the form as well via RHF
    /////////////////// FOR RESET VALUES ///////////////////////
    ///////////// From date-input-field.tsx //////////////////
    //   useEffect(() => {
    //     if (resetValues === 'all' || resetValues === 'filters') {
    //         setSelected((watchOperator && watchValue) || false);
    //         setOption(watchOperator && watchValue ? watchOperator : '');
    //     }
    // }, [resetValues]);
    ///////////// From metadata-input-field.tsx //////////////////
    //   useEffect(() => {
    //     if (resetValues === 'all' || resetValues === 'filters') {
    //         setSelected((watchOperator && watchValue) || false);
    //         setOption(watchOperator && watchValue ? watchOperator : '');
    //     }
    // }, [resetValues]);
    ///////////// From number-input-field.tsx //////////////////
    //   useEffect(() => {
    //     if (resetValues === 'all' || resetValues === 'filters') {
    //         setSelected((watchOperator && watchValue) || false);
    //         setOption(watchOperator && watchValue ? watchOperator : '');
    //     }
    // }, [resetValues]);
    ///////////// From string-input-field.tsx //////////////////
    //   useEffect(() => {
    //     if (resetValues === 'all' || resetValues === 'filters') {
    //         setSelected((watchOperator && watchValue) || false);
    //         setOption(watchOperator && watchValue ? watchOperator : '');
    //     }
    // }, [resetValues]);
    ///////////// From filter-options.tsx //////////////////
    // THIS IS NEEDS TO BE AT TOP LEVEL TO BE ABLE TO HANDLE RESET VALUES HERE
    //   useEffect(() => {
    //     if (!watchType || !AllowedSearchSyncUpdateTypes.includes(watchType as SearchSyncUpdateType)) {
    //         return;
    //     }
    //     const filterableAttributes = [...SearchSchema[watchType as SearchSyncUpdateType].filterableAttributes].filter((attribute) => {
    //         if (attribute?.disabledOn && environmentOptions.application) {
    //             if (attribute.disabledOn.includes(environmentOptions.application)) {
    //                 return false;
    //             }
    //         }
    //         return true;
    //     });
    //     if (filterableAttributes) {
    //         setFilterableAttributes([...filterableAttributes]);
    //     }
    // }, [watchType]);
    /////////////////// filters.tsx ///////////////////////
    //   const onResetDefaultsClick = () => {
    //     const newQuery: SourQuery = {
    //         type: defaultFilters?.query?.type || undefined,
    //         filters: defaultFilters?.query?.filters || {},
    //         sort: defaultFilters?.query?.sort || { field: undefined, direction: 'DESC' },
    //         query: defaultFilters?.query?.query || { query: '', restrictToFields: [] },
    //     };
    //     setValue('type', newQuery.type);
    //     setValue('filters', newQuery.filters);
    //     setValue('sort', newQuery.sort);
    //     setValue('query', newQuery.query);
    //     if (newQuery.type) setType(true);
    //     setLastSavedFilters({ filters: { ...newQuery.filters }, sort: newQuery.sort });
    //     handleResetDefaultFilters(newQuery);
    //     setFiltersOpen(false);
    // };
    /////////////////// FOR RESET VALUES ///////////////////////
    const values = useMemo(() => ({
        environmentOptions,
        onSubmit,
        onNavigate: externalNavigate,
        isLoading,
        filtersOpen,
        setFiltersOpen,
        defaultFilters,
        searchActive,
        setSearchActive,
        filterActive,
        setFilterActive,
        formInitialized,
        restriction,
        highlightActive,
        onResetValues,
        resetValues,
        handleResetDefaultFilters,
        handleCheckIfEqualToDefault,
        results,
        onSeeAllClick,
        onGlobalOnSeeAllClick,
        registerOnResultsHandler,
        searchOrFilterActive,
        page,
        setPage,
        size,
        setSize,
        searchResponse,
        setDefaultFilters,
        showEasyFilter,
        searchTerm,
        setSearchTerm,
        lockFilterType,
    }), [
        environmentOptions,
        results,
        externalNavigate,
        filtersOpen,
        setFiltersOpen,
        isLoading,
        defaultFilters,
        searchActive,
        setSearchActive,
        filterActive,
        setFilterActive,
        formInitialized,
        restriction,
        highlightActive,
        onResetValues,
        resetValues,
        handleResetDefaultFilters,
        handleCheckIfEqualToDefault,
        searchOrFilterActive,
        page,
        size,
        searchResponse,
        showEasyFilter,
        searchTerm,
        lockFilterType,
    ]);
    return (_jsx(SourSearchContext.Provider, { value: values, children: _jsx(FormProvider, { ...methods, children: children }) }));
};
//# sourceMappingURL=sour-search-context.js.map